import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import type { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router'
import styled from 'styled-components'

import SortableItineraryDays from './SortableItineraryDays'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { HeaderHeightContext } from 'contexts/HeaderStateContext'
import zIndex from 'styles/tools/z-index'
import DateRangeCarousel from 'tripPlanner/components/DateRangeCarousel'
import ItemCardSkeleton from 'tripPlanner/components/Skeleton/ItemCardSkeleton'
import ItineraryPreview from 'tripPlanner/components/Timeline/itineraryPreview'
import EditTripDates from 'tripPlanner/components/TripModal/Trips/EditTripDatesTP'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import LoadingPage from 'tripPlanner/pages/Loading'
import type { FullTrip } from 'tripPlanner/types/common'
import Group from 'components/utils/Group'
import { getStartItemsBySection, itinerarySectionElementId, itinerarySectionToString } from 'tripPlanner/utils/itinerary'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import awaitEndOfScroll from 'lib/promise/awaitEndOfScroll'
import AITriploadingStickyFooter from 'tripPlanner/components/EntryPoints/AITriploadingStickyFooter'

const MissingDatesSection = styled(VerticalSpacer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${(props) => props.theme.palette.neutral.default.eight};
  margin-bottom: ${rem(8)};
  padding: ${rem(32)} ${rem(24)};
  text-align: center;

  ${mediaQueryUp.tablet} {
    padding: ${rem(32)} ${rem(40)};
  }
`

const EmptyContent = styled.div`
  padding: ${rem(32)} ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(32)} ${rem(40)};
  }
`

const ItemsContainer = styled(Group)`
  background-color: ${(props) => props.theme.palette.neutral.default.six};
`

const StickyDateCarousel = styled(DateRangeCarousel)`
  position: sticky;
  z-index: ${zIndex.header + 1};

  @media print {
    display: none;
  }
`

function elementPagePosition(el: HTMLElement) {
  const rect = el.getBoundingClientRect()
  return rect.top + window.scrollY
}

interface Props extends RouteComponentProps {
  trip: FullTrip
  tripLoading: boolean
}

function TripItinerary({ trip, tripLoading }: Props) {
  const intl = useIntl()
  const openModal = useOpenModal()

  const { id: tripId, items: allItems, startDate, endDate } = trip
  const items = useMemo(() => {
    if (tripLoading || !allItems || allItems.length === 0) {
      return []
    }

    return allItems.filter(item =>
      (!item.alternativeGroup) || (item.alternativeGroup && item.startDate),
    )
  }, [allItems, tripLoading])

  const [selectedSection, setSelectedSection] = useState<string | undefined>()
  const scrolling = useRef(false)
  const sectionVisibilities = useRef<Record<string, boolean>>({})
  const [isVisible] = useState(true)

  const startItemsBySection = useMemo(
    () => getStartItemsBySection(items, startDate, endDate),
    [items, startDate, endDate],
  )

  const sections = useMemo(() => (
    startItemsBySection.map(p => p.date)
  ), [startItemsBySection])

  const determineSelectedSectionFromVisibility = useCallback(() => {
    const sectionsAsString = sections.map((section) => itinerarySectionToString(section))
    const firstVisible = sectionsAsString.find((section) => sectionVisibilities.current[section])
    setSelectedSection(firstVisible ?? sectionsAsString[0])
  }, [sections])

  const onSectionVisibilityChange = useCallback(
    (section: string, isVisible: boolean) => {
      sectionVisibilities.current[section] = isVisible
      if (!scrolling.current) {
        determineSelectedSectionFromVisibility()
      }
    },
    [determineSelectedSectionFromVisibility],
  )

  const onClickSetDates = useCallback(() => {
    openModal(EditTripDates, { tripId })
  }, [openModal, tripId])

  const { heights } = useContext(HeaderHeightContext)
  const carouselStyle = useMemo(
    () => ({ top: heights.tripPlanner }),
    [heights.tripPlanner],
  )
  const { canEdit } = useTripPermissions(tripId)

  const headerOffset = heights.tripPlanner + heights.tripPlannerDateCarousel
  const hasValidOffset = heights.tripPlanner > 0 && heights.tripPlannerDateCarousel > 0

  const scrollToSection = useCallback((section: string, behavior: ScrollBehavior = 'smooth') => {
    const sectionElement = document.getElementById(itinerarySectionElementId(section))
    if (sectionElement) {
      setSelectedSection(section)
      scrolling.current = true
      const verticalDateOffSet = elementPagePosition(sectionElement) ?? 0
      const totalOffset = verticalDateOffSet - headerOffset
      window.scrollTo({ top: totalOffset, behavior })
      awaitEndOfScroll().then(() => {
        scrolling.current = false
        determineSelectedSectionFromVisibility()
      })
    }
  }, [determineSelectedSectionFromVisibility, headerOffset])

  const initialScrollEvaluated = useRef(false)
  useEffect(() => {
    if (trip && !initialScrollEvaluated.current && hasValidOffset) {
      initialScrollEvaluated.current = true
      if (startDate && endDate && moment().isBetween(startDate, endDate, 'days', '[]')) {
        scrollToSection(moment().format(ISO_DATE_FORMAT), 'instant')
      }
    }
  })

  if (!trip) return <LoadingPage />

  if (startDate && endDate) {
    return (
      <>
        <StickyDateCarousel
          style={carouselStyle}
          startDate={startDate}
          endDate={endDate}
          sections={sections}
          selectedSection={selectedSection}
          items={items}
          onClick={scrollToSection}
        />
        <SortableItineraryDays
          trip={trip}
          items={items}
          startItemsBySection={startItemsBySection}
          onSectionVisibilityChange={onSectionVisibilityChange}
          headerOffset={headerOffset}
          tripLoading={tripLoading}
        />
        <AITriploadingStickyFooter
          trip={trip}
          isVisible={isVisible}
          generationStatus={trip.generationStatus}
        />

      </>
    )
  }

  if (canEdit) {
    return (
      <MissingDatesSection gap={24}>
        <BodyText variant="large">
          {intl.formatMessage({
            id: 'j03lVTZuZmEvmF3mTYfN',
            defaultMessage: 'Click to add dates to your trip to start creating your itinerary.',
            description: 'Instruction text prompting user to add dates to start building their trip itinerary',
          })}
        </BodyText>
        <TextButton
          kind="primary"
          size="large"
          onClick={onClickSetDates}
          startIcon={<LineCalendarIcon />}
        >
          {intl.formatMessage({
            id: '93MyfH5iFfjG7WFhHKXv',
            defaultMessage: 'Add dates',
            description: 'Button to add dates to a trip in the missing-dates empty state',
          })}
        </TextButton>
        <ItineraryPreview />
      </MissingDatesSection>
    )
  } else {
    return (
      <div>
        <EmptyContent>
          <Heading colour="neutral-three" variant="heading4">
            {intl.formatMessage({
              id: 'U0NGZ0z-I4QuL6Qj1kfO',
              defaultMessage: 'One day...',
              description: 'Heading shown to non-editors when a trip has no dates set yet',
            })}
          </Heading>
          <BodyText colour="neutral-three" variant="medium">
            {intl.formatMessage({
              id: 'QmoBWy5FGemGFixoUE1Q',
              defaultMessage: 'With specific dates for trip items, you’ll see your day-by-day view',
              description: 'Explanation shown to non-editors that a day-by-day itinerary appears once dates are added',
            })}
          </BodyText>
        </EmptyContent>
        <ItemsContainer direction="vertical" gap={8}>
          <ItemCardSkeleton />
          <ItemCardSkeleton />
          <ItemCardSkeleton />
        </ItemsContainer>
      </div>
    )
  }
}

export default withRouter(TripItinerary)
